/**
 * scoutErrorCapture — Sentry-compatible error capture that routes to
 * scout_activity_log instead of a third-party SaaS. For an all-AI fleet
 * with Supabase SSoT (D-23), Sentry's human-oriented dashboard is
 * redundant — an agent just runs SQL against scout_activity_log.
 *
 * Contract mirrors Sentry's API so the eventual replacement of
 * @sentry/react is a mechanical rename:
 *   Sentry.captureException(err, { tags, extra })  ->  captureError(err, { tags, extra })
 *
 * Events emitted:
 * - captured_error: single-event error capture (deliberate catch-block use)
 * - uncaught_error: window.onerror firings
 * - unhandled_rejection: Promise rejection with no .catch
 *
 * Never throws. Never blocks UI. Safe to call from any context, including
 * inside the error path itself.
 */

import { logScoutEvent } from './scoutLog'

export interface CaptureContext {
  /** Stable labels for grouping/filtering in SQL (e.g. feature=prequal-submit). */
  tags?: Record<string, string>
  /** Per-occurrence context (ids, state). Goes into the payload verbatim. */
  extra?: Record<string, unknown>
  /** Severity hint. Defaults to 'error'. */
  level?: 'error' | 'warning' | 'info'
}

interface DescribedError {
  name: string
  message: string
  stack: string | null
}

/**
 * Normalise any thrown value into a readable {name, message, stack}.
 *
 * Supabase's PostgrestError and similar SDK errors are plain objects with
 * {code, message, details, hint} — NOT instances of Error. Without this
 * normalisation, `String(error)` collapses them to `[object Object]` and
 * every support ticket becomes a forensic exercise.
 */
function describeError(err: unknown): DescribedError {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack ?? null }
  }
  if (err && typeof err === 'object') {
    const o = err as Record<string, unknown>
    const parts = [o.code, o.message, o.details, o.hint].filter(
      (p): p is string => typeof p === 'string' && p.length > 0,
    )
    if (parts.length > 0) {
      return {
        name: typeof o.name === 'string' ? o.name : 'PostgrestError',
        message: parts.join(' — '),
        stack: typeof o.stack === 'string' ? o.stack : null,
      }
    }
    try {
      return { name: 'unknown', message: JSON.stringify(err), stack: null }
    } catch {
      return { name: 'unknown', message: '[unserialisable error]', stack: null }
    }
  }
  return { name: 'unknown', message: String(err), stack: null }
}

/**
 * Capture an error into scout_activity_log. Drop-in replacement for
 * Sentry.captureException. Never throws.
 */
export function captureError(err: unknown, ctx: CaptureContext = {}): void {
  try {
    const { name, message, stack } = describeError(err)
    logScoutEvent('captured_error', {
      error_name: name,
      error_message: message,
      error_stack: stack,
      tags: ctx.tags ?? {},
      extra: ctx.extra ?? {},
      level: ctx.level ?? 'error',
    })
  } catch {
    // Error-path code must never throw.
  }
}

/**
 * Install window-level handlers for uncaught exceptions and unhandled
 * promise rejections. Idempotent — safe to call multiple times.
 *
 * These are the ONE thing Sentry's auto-capture gave us that manual
 * try/catch doesn't. Without this, a crash in a non-wrapped code path
 * goes silent.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return
  const w = window as Window & { __SCOUT_GLOBAL_ERROR_HANDLERS__?: boolean }
  if (w.__SCOUT_GLOBAL_ERROR_HANDLERS__) return
  w.__SCOUT_GLOBAL_ERROR_HANDLERS__ = true

  window.addEventListener('error', (ev) => {
    try {
      const err = ev.error ?? new Error(ev.message)
      const { name, message, stack } = describeError(err)
      logScoutEvent('uncaught_error', {
        error_name: name,
        error_message: message,
        error_stack: stack,
        filename: ev.filename || null,
        lineno: ev.lineno || null,
        colno: ev.colno || null,
      })
    } catch {
      // Never re-throw from the error handler itself.
    }
  })

  window.addEventListener('unhandledrejection', (ev) => {
    try {
      const { name, message, stack } = describeError(ev.reason)
      logScoutEvent('unhandled_rejection', {
        error_name: name,
        error_message: message,
        error_stack: stack,
      })
    } catch {
      // Never re-throw from the rejection handler itself.
    }
  })
}
